import { progressApi } from '../api/progress';
import { reachedTasbeehGoals, tasbeehGoalReachedError } from './tasbeehGoals';
import { readOfflineTasbeeh, pendingOfflineDay } from './offlineTasbeehStorage';

export const TASBEEH_CHANGED = 'amposter:tasbeeh-counter-changed';
export const TASBEEH_STORAGE_SIGNAL = 'amposter:tasbeeh-counter-signal';
export const utcTasbeehDay = () => new Date().toISOString().slice(0, 10);
const MAX = 1000000000000;
const valid = value => Number.isSafeInteger(value) && value >= 0 ? Math.min(MAX, value) : 0;
const entryKey = (dhikr, day) => JSON.stringify([day, dhikr]);
let opening;
const flushing = new Map();

function open() {
  if (!opening) opening = new Promise((resolve, reject) => {
    const request = indexedDB.open('amposter-tasbeeh-counter', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('accounts', { keyPath: 'accountId' });
    request.onsuccess = () => {
      request.result.onversionchange = () => { request.result.close(); opening = null; };
      resolve(request.result);
    };
    request.onerror = () => { opening = null; reject(request.error); };
  });
  return opening;
}
async function update(accountId, mutate) {
  if (!accountId || accountId === 'guest') return null;
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('accounts', mutate ? 'readwrite' : 'readonly');
    const store = tx.objectStore('accounts'), request = store.get(accountId);
    let row;
    request.onsuccess = () => {
      row = request.result || { accountId, deviceId: crypto.randomUUID(), counts: {}, totals: {}, daily: {}, goal: 100, goalUpdatedAt: 0, streak: 0 };
      if (mutate) { mutate(row); store.put(row); }
    };
    tx.oncomplete = () => resolve(row);
    tx.onerror = tx.onabort = () => reject(tx.error || new Error('Tasbeeh storage unavailable'));
  });
}
function announce(accountId) {
  window.dispatchEvent(new CustomEvent(TASBEEH_CHANGED, { detail: { accountId } }));
  try { localStorage.setItem(TASBEEH_STORAGE_SIGNAL, JSON.stringify({ accountId, nonce: crypto.randomUUID() })); } catch {}
}
export const readTasbeehCounter = accountId => update(accountId);

const LEGACY_KEY = 'tasbeeh_pending_increments';
const LEGACY_CLAIM = '__legacy_tasbeeh_claim_v1__';
export async function readLegacyTasbeehRecovery() {
  try {
    const marker = await readTasbeehCounter(LEGACY_CLAIM);
    if (marker?.claimed) return null;
    const raw = localStorage.getItem(LEGACY_KEY), values = Object.values(JSON.parse(raw || '{}'));
    const entries = values.filter(entry => typeof entry?.tasbeeh === 'string' && entry.tasbeeh.trim() && entry.tasbeeh.length <= 160 && valid(entry.count) > 0);
    return entries.length ? { raw, entries, total: entries.reduce((sum, entry) => sum + valid(entry.count), 0) } : null;
  } catch { return null; }
}
export async function recoverLegacyTasbeeh(accountId) {
  if (!accountId || accountId === 'guest') throw new Error('Choose an account first');
  const legacy = await readLegacyTasbeehRecovery();
  if (!legacy) return;
  const db = await open();
  await new Promise((resolve, reject) => {
    const tx = db.transaction('accounts', 'readwrite'), store = tx.objectStore('accounts');
    const claim = store.get(LEGACY_CLAIM);
    claim.onsuccess = () => {
      if (claim.result?.claimed) return;
      const request = store.get(accountId);
      request.onsuccess = () => {
        const row = request.result || { accountId, deviceId: crypto.randomUUID(), counts: {}, totals: {}, daily: {}, goal: 100, goalUpdatedAt: 0, streak: 0 };
        legacy.entries.forEach(entry => {
          // Old storage had no owner or reliable date: recover only with the
          // user's confirmation and never grant old competition rewards.
          const activityDate = '1970-01-01', dhikr = entry.tasbeeh.trim(), key = entryKey(dhikr, activityDate);
          const previous = row.counts[key] || { dhikr, activityDate, total: 0, acknowledged: 0 };
          row.counts[key] = { ...previous, total: Math.min(MAX, previous.total + valid(entry.count)) };
        });
        store.put(row); store.put({ accountId: LEGACY_CLAIM, claimed: accountId });
      };
    };
    tx.oncomplete = resolve; tx.onerror = tx.onabort = () => reject(tx.error || new Error('Recovery failed'));
  });
  try { if (localStorage.getItem(LEGACY_KEY) === legacy.raw) localStorage.removeItem(LEGACY_KEY); } catch {}
  announce(accountId);
}

export async function enqueueTasbeeh(accountId, dhikr, { enforceGoals = false } = {}) {
  if (!accountId || !dhikr || dhikr.length > 160) throw new Error('Invalid tasbeeh');
  const day = utcTasbeehDay();
  const offlineSnapshot = enforceGoals ? await readOfflineTasbeeh(accountId) : null;
  let blocked = [];
  const row = await update(accountId, value => {
    if (enforceGoals) {
      blocked = reachedTasbeehGoals(accountId,dhikr,tasbeehCounterView(value,dhikr,day,offlineSnapshot),day);
      if (blocked.length) return;
    }
    const key = entryKey(dhikr, day);
    const entry = value.counts[key] || { dhikr, activityDate: day, total: 0, acknowledged: 0 };
    value.counts[key] = { ...entry, total: Math.min(MAX, valid(entry.total) + 1) };
  });
  if (blocked.length) throw tasbeehGoalReachedError(blocked);
  announce(accountId);
  return enforceGoals ? { ...row, offlineSnapshot } : row;
}
function mergeProgress(row, progress) {
  if (!progress?.dhikr) return;
  row.totals[progress.dhikr] = Math.max(valid(row.totals[progress.dhikr]), valid(progress.totalCount));
  const day = progress.activityDate || utcTasbeehDay();
  row.daily[day] = Math.max(valid(row.daily[day]), valid(progress.todayCount));
  row.rewardedDaily ||= {};
  row.rewardedDaily[day] = Math.max(valid(row.rewardedDaily[day]), valid(progress.rewardedTodayCount ?? progress.todayCount));
  row.dhikrDaily ||= {};
  row.offlineDailyAccepted ||= {};
  if (progress.offlineDailyAccepted) {
    const accepted = row.offlineDailyAccepted[day] ||= {};
    Object.entries(progress.offlineDailyAccepted).forEach(([key,count])=>{accepted[key]=Math.max(valid(accepted[key]),valid(count));});
  }
  if (Number.isSafeInteger(progress.dhikrTodayCount)) row.dhikrDaily[entryKey(progress.dhikr,day)] = Math.max(valid(row.dhikrDaily[entryKey(progress.dhikr,day)]),valid(progress.dhikrTodayCount));
  row.dhikrGoals ||= {};
  const ownVersion = Date.parse(progress.dhikrGoalUpdatedAt) || 0;
  if ('dhikrGoal' in progress && ownVersion >= (row.dhikrGoals[progress.dhikr]?.updatedAt || 0)) {
    row.dhikrGoals[progress.dhikr] = { goal:progress.dhikrGoal, updatedAt:ownVersion };
  }
  if (day === utcTasbeehDay()) row.streak = valid(progress.streak);
  const version = Date.parse(progress.goalUpdatedAt) || 0;
  if (version >= row.goalUpdatedAt && progress.goal >= 10 && progress.goal <= 10000) {
    row.goal = progress.goal; row.goalUpdatedAt = version;
  }
}
export async function acceptTasbeehUpdate(accountId, message) {
  if (message.accountId && message.accountId !== accountId) return null;
  const row = await update(accountId, value => {
    // The acknowledgement and the server total move together. A websocket
    // acknowledgement arriving before HTTP must not double-count local taps.
    if (message.deviceId === value.deviceId) {
      const entry = value.counts[entryKey(message.dhikr, message.activityDate)];
      if (entry) entry.acknowledged = Math.max(entry.acknowledged, Math.min(entry.total, valid(message.acceptedCount)));
    }
    if (message.progress) mergeProgress(value, message.progress);
    value.dhikrDaily ||= {};
    Object.entries(message.dhikrDailyCounts || {}).forEach(([dhikr,count]) => {
      const key = entryKey(dhikr,message.progress?.activityDate || utcTasbeehDay());
      value.dhikrDaily[key] = Math.max(valid(value.dhikrDaily[key]),valid(count));
    });
    Object.entries(message.totals || {}).forEach(([dhikr, count]) => {
      value.totals[dhikr] = Math.max(valid(value.totals[dhikr]), valid(count));
    });
  });
  announce(accountId);
  return row;
}
export function tasbeehCounterView(row, dhikr, day = utcTasbeehDay(), offlineRow = row?.offlineSnapshot) {
  const pending = Object.values(row?.counts || {});
  const amount = entry => Math.max(0, valid(entry.total) - valid(entry.acknowledged));
  const offlineAccepted = row?.offlineDailyAccepted?.[day];
  return {
    totalCount: valid(row?.totals?.[dhikr]) + pending.filter(entry => entry.dhikr === dhikr).reduce((sum, entry) => sum + amount(entry), 0),
    todayCount: valid(row?.daily?.[day]) + pending.filter(entry => entry.activityDate === day).reduce((sum, entry) => sum + amount(entry), 0) + pendingOfflineDay(offlineRow,day,undefined,offlineAccepted),
    rewardedTodayCount: valid(row?.rewardedDaily?.[day] ?? row?.daily?.[day]) + pending.filter(entry => entry.activityDate === day).reduce((sum, entry) => sum + amount(entry), 0),
    dhikrTodayCount: valid(row?.dhikrDaily?.[entryKey(dhikr,day)]) + pending.filter(entry => entry.activityDate === day && entry.dhikr === dhikr).reduce((sum,entry) => sum + amount(entry),0) + pendingOfflineDay(offlineRow,day,dhikr,offlineAccepted),
    offlineDailyAccepted:offlineAccepted,
    dhikrGoal: row?.dhikrGoals?.[dhikr]?.goal ?? null, dhikrGoalUpdatedAt:row?.dhikrGoals?.[dhikr]?.updatedAt || 0,
    pending: pending.reduce((sum, entry) => sum + amount(entry), 0),
    goal: row?.goal || 100, goalUpdatedAt:row?.goalUpdatedAt || 0, streak: row?.streak || 0,
  };
}
export function flushTasbeehCounter(accountId, signal) {
  const previous = flushing.get(accountId);
  if (previous) {
    // Reconnect or a maintenance-status refresh can start a new effect before
    // the cancelled request settles. The new caller must drain the queue,
    // rather than inherit that cancelled run and leave its taps stranded.
    if (previous.signal?.aborted && !signal?.aborted) {
      return previous.promise.catch(() => {}).then(() => flushTasbeehCounter(accountId, signal));
    }
    return previous.promise;
  }
  const run = (async () => {
    while (!signal?.aborted && navigator.onLine !== false) {
      const row = await readTasbeehCounter(accountId);
      if (!row || signal?.aborted) return;
      const entry = Object.values(row.counts).find(value => value.total > value.acknowledged);
      if (!entry) return;
      const sentCount = Math.min(entry.total, entry.acknowledged + 100);
      const response = await progressApi.syncTasbeeh({ accountId, deviceId: row.deviceId, dhikr: entry.dhikr,
        activityDate: entry.activityDate, totalCount: sentCount }, signal);
      if (response?.deviceId !== row.deviceId || response.dhikr !== entry.dhikr || response.activityDate !== entry.activityDate ||
          !Number.isSafeInteger(response.acceptedCount) || response.acceptedCount < sentCount || response.progress?.dhikr !== entry.dhikr) {
        throw new Error('Tasbeeh acknowledgement is incomplete');
      }
      await acceptTasbeehUpdate(accountId, response);
    }
  })().finally(() => { if (flushing.get(accountId)?.promise === run) flushing.delete(accountId); });
  flushing.set(accountId, { promise: run, signal });
  return run;
}
